/**
 * The things in a kitchen that a kitchen maker does not make.
 *
 * A fridge, a dishwasher and an oven are bought, not built, and the maker's range carries
 * only the SPACE for each: `t900f` is a 900 gap, `b600dw` is a 600 gap, `t600o` is a tower
 * with a hole in it. What stands in that space has its own size, and it is not the size of
 * the space -- which matters, because a family fridge is 700 deep and a bench is 560, so it
 * stands 140 proud, which is a thing everyone who has ever planned a kitchen knows and
 * nothing in this app used to show.
 *
 * Until now all three drew as ONE GREY BOX. The fridge branch even had a ternary that
 * chose between the same material twice. An oven had no branch at all, so a 2100 oven tower
 * drew as a plain cupboard: the one cabinet a person looks for to check the layout, and it
 * was invisible.
 *
 * Sizes below are real appliances, in millimetres, of the kind sold in Australia. They are
 * a maker-independent fact -- a Bosch dishwasher is 598 wide whoever builds the kitchen --
 * so they live here rather than in maker.js, and a maker who stocks something different
 * overrides by giving the spec its own `appliance` block.
 */
import * as THREE from 'three'
import { MM } from './units.js'

/** What actually stands in the space, in millimetres. */
export const SIZE = {
  // A French-door family fridge. 700 deep is the number that makes it stand proud of a
  // 560 bench, which is the fact the plan and the elevation both need.
  fridge: { w: 900, h: 1750, d: 700 },
  // The European standard, and the reason a "600 dishwasher space" is 600 and not 598.
  dishwasher: { w: 598, h: 820, d: 600 },
  // A single wall oven. 595 square is the cavity every brand builds to.
  oven: { w: 595, h: 595, d: 560 },
}

/** Where an oven sits in its tower: the height of the bottom of the aperture. */
export const OVEN_AT = 850

const box = (w, h, d, mat) => new THREE.Mesh(new THREE.BoxGeometry(w * MM, h * MM, d * MM), mat)

/**
 * A fridge: a door with a seam down it, a long handle, and the depth it really is.
 *
 * Drawn at SIZE.fridge.d rather than the space's depth, so it stands proud of the bench.
 * Centred in its space, because a 900 fridge in a 950 space has 25mm either side.
 */
export function buildFridge(item, spec, look, lim) {
  const g = new THREE.Group()
  const s = SIZE.fridge
  const w = Math.min(item.w - 20, s.w)
  const h = Math.min(spec.h ?? s.h, s.h)
  const x0 = (item.w - w) / 2

  /*
   * FREESTANDING OR BUILT IN, which is the choice a person actually makes about a fridge.
   *
   *   freestanding  the machine on show, in stainless, 700 deep so it stands proud of the
   *                 bench -- which is what most kitchens have and what this always drew.
   *   integrated    a cabinet door on the front, and the fridge set back to the cabinet's
   *                 own depth so the doors line up with everything else in the run. It is
   *                 the same fridge; what changes is the face and where the face sits.
   *
   * On the floor either way. A fridge has no plinth under it: it was drawn from the top of
   * the kick and floated 150mm above the floorboards, which shows from any angle low enough
   * to see the floor at all.
   */
  const built = look.fittings?.fridge?.style === 'integrated'
  const foot = -lim.kickHeight
  const depth = built ? (spec.d ?? lim.baseDepth) : s.d
  const body = box(w, h, depth, built ? look.doorMat : look.appliance.panel)
  body.position.set((x0 + w / 2) * MM, (foot + h / 2) * MM, depth / 2 * MM)
  body.castShadow = body.receiveShadow = true
  g.add(body)

  // The seam between the two doors, and one handle each side of it. A flat steel box reads
  // as a filing cabinet; two lines and two handles read as a fridge from across the room.
  // A built-in one gets the kitchen's own handle instead, because that is the whole point.
  const seam = box(6, h - 60, 4, built ? look.doorMat : look.appliance.ring)
  seam.position.set((x0 + w / 2) * MM, (foot + h / 2) * MM, (depth + 2) * MM)
  g.add(seam)
  for (const side of [-1, 1]) {
    if (built && look.handle?.style === 'none') break
    const grip = box(18, built ? h * 0.3 : h * 0.42, 26,
      built ? (look.handleMat ?? look.appliance.steel) : look.appliance.steel)
    grip.position.set((x0 + w / 2 + side * 46) * MM, (foot + h * 0.62) * MM, (depth + 14) * MM)
    grip.castShadow = true
    g.add(grip)
  }
  // The gap above it, to the top of the tower, so the space reads as a space.
  /*
   * THE SPACE ABOVE IT IS A CUPBOARD, not a panel.
   *
   * It was one blank box in door material -- which is what the gap between the top of a
   * fridge and the top of the tower looks like only if nobody has been sold anything to put
   * there. In a real kitchen that is a cupboard: it is where the platters and the slow
   * cooker live, and it is the reason a fridge goes in a tower rather than standing in a
   * gap. A carcass, a door per 500mm of width, and a handle along the bottom edge, which is
   * where the handle goes on something above head height.
   */
  const above = (spec.h ?? s.h) - h - lim.kickHeight
  if (above > 40) {
    const shell = box(item.w, above, lim.baseDepth, look.carcass)
    shell.position.set(item.w / 2 * MM, (foot + h + above / 2) * MM, lim.baseDepth / 2 * MM)
    shell.castShadow = shell.receiveShadow = true
    g.add(shell)

    const y = foot + h + above / 2
    const face = (lim.baseDepth + 9) * MM
    const leaves = Math.max(1, Math.round(item.w / 500))
    const each = (item.w - 6 * (leaves + 1)) / leaves
    for (let i = 0; i < leaves; i++) {
      const x = 6 + i * (each + 6) + each / 2
      const door = box(each, above - 6, 18, look.doorMat)
      door.position.set(x * MM, y * MM, face)
      door.castShadow = true
      g.add(door)
      if (look.handle?.style && look.handle.style !== 'none' && above > 180) {
        const grip = box(Math.min(each - 60, 220), 14, 22, look.handleMat ?? look.appliance.steel)
        grip.position.set(x * MM, (foot + h + 50) * MM, face + 14 * MM)
        grip.castShadow = true
        g.add(grip)
      }
    }
  }
  return g
}

/**
 * A dishwasher: a stainless front, a control strip along the top, and a recessed toe.
 *
 * Set back 20mm from the cabinet fronts either side of it, which is how an unintegrated
 * machine sits and what makes it legible as one.
 */
export function buildDishwasher(item, spec, look, lim) {
  const g = new THREE.Group()
  const s = SIZE.dishwasher
  const w = Math.min(item.w - 4, s.w)
  const x0 = (item.w - w) / 2
  const d = Math.min(s.d, lim.baseDepth) - 20

  /*
   * IT GOES UNDER THE BENCH, AND IT STANDS ON THE FLOOR.
   *
   * A cabinet's local origin is the top of the kick, so drawing the machine from y = 0 at
   * its catalogue height of 820 put its top 820 above the kick and about 100 THROUGH the
   * benchtop -- a dishwasher taller than the bench, which is what it looked like. The 820 is
   * the machine's own height on its feet; the CAVITY it goes in is the kick plus the base,
   * and the door front covers the lot. So it runs from the floor to the underside of the
   * top, and it has no toe kick in front of it, because a dishwasher does not.
   */
  const bottom = -lim.kickHeight
  const h = lim.kickHeight + lim.baseHeight
  const top = lim.baseHeight

  /*
   * WHAT THE FRONT IS, which is the whole of the choice.
   *
   *   integrated    a cabinet door, and the machine disappears. The controls face upward on
   *                 the top edge, so nothing of it shows from the room at all.
   *   semi          a cabinet door with the control panel showing in a strip above it: the
   *                 common compromise, and the one you can actually read while it runs.
   *   freestanding  the machine on show, in stainless.
   *
   * The machine behind it is the same machine either way, so only the face changes.
   */
  const style = look.fittings?.dishwasher?.style ?? 'freestanding'
  const panelH = style === 'semi' ? h - 120 : h
  const front = style === 'freestanding' ? look.appliance.panel : look.doorMat

  const body = box(w, panelH, d, front)
  body.position.set((x0 + w / 2) * MM, (bottom + panelH / 2) * MM, d / 2 * MM)
  body.castShadow = body.receiveShadow = true
  g.add(body)

  if (style === 'semi') {
    const strip = box(w, 120, d, look.appliance.panel)
    strip.position.set((x0 + w / 2) * MM, (top - 60) * MM, d / 2 * MM)
    strip.castShadow = strip.receiveShadow = true
    g.add(strip)
    const controls = box(w - 30, 34, 6, look.appliance.ring)
    controls.position.set((x0 + w / 2) * MM, (top - 60) * MM, (d + 3) * MM)
    g.add(controls)
  }

  if (style === 'freestanding') {
    const controls = box(w - 20, 46, 6, look.appliance.ring)
    controls.position.set((x0 + w / 2) * MM, (top - 34) * MM, (d + 3) * MM)
    g.add(controls)
  }

  // An integrated machine wears the kitchen's own handle; the other two have their own.
  const grip = box(w - 90, 16, 24,
    style === 'integrated' ? look.handleMat ?? look.appliance.steel : look.appliance.steel)
  grip.position.set((x0 + w / 2) * MM,
    (style === 'semi' ? top - 150 : top - 78) * MM, (d + 12) * MM)
  grip.castShadow = true
  g.add(grip)
  return g
}

/**
 * An oven tower: a cupboard, an oven, a cupboard.
 *
 * The aperture is at OVEN_AT, which is where an oven goes so the shelf comes out at
 * waist height rather than at the floor. Above and below it the tower is ordinary
 * cabinetry, which is why this returns the doors as well as the appliance -- the caller
 * would otherwise have to know the aperture's position to lay them out around it.
 */
export function buildOven(item, spec, look, lim) {
  const g = new THREE.Group()
  const s = SIZE.oven
  const w = item.w
  const h = spec.h ?? 2100
  const d = spec.d ?? lim.baseDepth
  const carcassDepth = d - 20

  const body = box(w, h, carcassDepth, look.carcass)
  body.position.set(w / 2 * MM, h / 2 * MM, carcassDepth / 2 * MM)
  body.castShadow = body.receiveShadow = true
  g.add(body)

  /*
   * ONE OVEN OR TWO, stacked, in the same tower.
   *
   * A double oven is not a different cabinet -- it is the same 600 tower with a taller
   * aperture and a shorter cupboard above it, which is exactly how it is sold and installed.
   * The pair sits ON the single's height so the lower oven stays at the height an oven is
   * meant to be at: `OVEN_AT` is where the bottom of the aperture goes, and the second one
   * goes above the first rather than the first dropping to the floor to make room.
   */
  const stack = (look.fittings?.oven?.stack ?? [s.h]).slice(0, 3)
  const bottom = OVEN_AT, top = OVEN_AT + stack.reduce((a, b) => a + b, 0)
  const face = (carcassDepth + 9) * MM

  // The ovens themselves: a dark glass door in a steel surround, with a bar across it. A
  // compact oven is the same thing shorter, so the only thing that varies is its height.
  let y0 = bottom
  for (const oh of stack) {
    const surround = box(w - 8, oh, 22, look.appliance.panel)
    surround.position.set(w / 2 * MM, (y0 + oh / 2) * MM, face)
    surround.castShadow = true
    g.add(surround)
    const glass = box(w - 90, Math.max(60, oh - 150), 8, look.appliance.glass)
    glass.position.set(w / 2 * MM, (y0 + oh / 2 - 14) * MM, face + 14 * MM)
    g.add(glass)
    const bar = box(w - 70, 20, 28, look.appliance.steel)
    bar.position.set(w / 2 * MM, (y0 + oh - 52) * MM, face + 22 * MM)
    bar.castShadow = true
    g.add(bar)
    y0 += oh
  }

  /*
   * AND THE CABINETRY AROUND IT, which is most of the tower.
   *
   * These were two blank panels in door material -- true in outline and wrong in the room,
   * because the space under an oven is not a blank, it is where the trays and the oven
   * dishes go. A real tower is DRAWERS below (two of them, the height an oven dish needs)
   * and a CUPBOARD above, and each has a handle on it. Without the handles the tower reads
   * as an appliance with two panels bolted on, which is exactly what it looked like.
   */
  const grip = (x, y, len, tall) => {
    if (!look.handle?.style || look.handle.style === 'none') return
    const m = tall
      ? box(14, Math.min(len, 220), 22, look.handleMat ?? look.appliance.steel)
      : box(Math.min(len, 220), 14, 22, look.handleMat ?? look.appliance.steel)
    m.position.set(x * MM, y * MM, face + 14 * MM)
    m.castShadow = true
    g.add(m)
  }

  // Below: drawers. Two if there is room for two that are worth opening, else one.
  const belowH = bottom - 6
  if (belowH > 100) {
    const n = belowH >= 500 ? 2 : 1
    const each = (belowH - 6 * (n - 1)) / n
    for (let i = 0; i < n; i++) {
      const y0 = 3 + i * (each + 6)
      const front = box(w - 6, each, 18, look.doorMat)
      front.position.set(w / 2 * MM, (y0 + each / 2) * MM, face)
      front.castShadow = true
      g.add(front)
      grip(w / 2, y0 + each - 60, w - 120, false)
    }
  }

  // Above: a cupboard, one door per 500mm of width, handles at the bottom edge where a
  // hand reaches them.
  const aboveH = h - top - 6
  if (aboveH > 100) {
    const leaves = Math.max(1, Math.round(w / 500))
    const each = (w - 6 * (leaves + 1)) / leaves
    for (let i = 0; i < leaves; i++) {
      const x = 6 + i * (each + 6) + each / 2
      const door = box(each, aboveH, 18, look.doorMat)
      door.position.set(x * MM, (top + 3 + aboveH / 2) * MM, face)
      door.castShadow = true
      g.add(door)
      grip(x, top + 60, each - 60, false)
    }
  }
  return g
}

/**
 * The sink and the cooktop, cut into the benchtop.
 *
 * Not because a maker supplies them -- they mostly do not -- but because a benchtop with a
 * hole in it is a kitchen and a benchtop without one is a shelf. They are the two objects
 * that make the picture read, and they cost about forty lines between them.
 */
export function buildAppliance(role, w, limits, mat) {
  const g = new THREE.Group()
  // Measured from the CABINET's own origin, which is already standing on its kick. Counting
  // the kick again here put the sink 150mm above the stone -- a bowl sitting on the bench
  // rather than cut into it, which reads as a box somebody left out.
  const topY = (limits.baseHeight + limits.benchtopThickness) * MM
  const d = limits.baseDepth

  if (role === 'sink') {
    const bw = Math.min(w - 120, 780)
    // The bowl, as a recess: an inset box below the benchtop line reads as depth because the
    // shadow falls into it.
    const bowl = new THREE.Mesh(
      new THREE.BoxGeometry(bw * MM, 180 * MM, 420 * MM), mat.steel)
    bowl.position.set(w / 2 * MM, topY - 92 * MM, (d * 0.52) * MM)
    bowl.receiveShadow = true
    g.add(bowl)
    // A rim, so the bowl has an edge rather than floating in the stone.
    const rim = new THREE.Mesh(
      new THREE.BoxGeometry((bw + 24) * MM, 6 * MM, 444 * MM), mat.steel)
    rim.position.set(w / 2 * MM, topY + 2 * MM, (d * 0.52) * MM)
    g.add(rim)
    // The tap, which is the thing that says "sink" from across a room.
    const spout = new THREE.Mesh(new THREE.CylinderGeometry(11 * MM, 11 * MM, 300 * MM, 12), mat.steel)
    spout.position.set(w / 2 * MM, topY + 150 * MM, (d * 0.2) * MM)
    spout.castShadow = true
    g.add(spout)
    const arc = new THREE.Mesh(new THREE.TorusGeometry(70 * MM, 11 * MM, 8, 16, Math.PI / 2), mat.steel)
    arc.position.set(w / 2 * MM, topY + 300 * MM, (d * 0.2) * MM)
    arc.rotation.y = Math.PI / 2
    arc.castShadow = true
    g.add(arc)
  }

  if (role === 'cooktop') {
    const cw = Math.min(w - 100, 700)
    const plate = new THREE.Mesh(new THREE.BoxGeometry(cw * MM, 8 * MM, 480 * MM), mat.glass)
    plate.position.set(w / 2 * MM, topY + 4 * MM, (d * 0.5) * MM)
    plate.receiveShadow = true
    g.add(plate)
    // Four rings. Small, dark, and the only thing that distinguishes a cooktop from a chopping board.
    for (const [ox, oz] of [[-0.24, -0.11], [0.24, -0.11], [-0.24, 0.11], [0.24, 0.11]]) {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(58 * MM, 4 * MM, 6, 20), mat.ring)
      ring.rotation.x = -Math.PI / 2
      ring.position.set((w / 2 + ox * cw) * MM, topY + 9 * MM, (d * 0.5 + oz * 480) * MM)
      g.add(ring)
    }
  }

  return g
}
